import React, { useContext, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useRouter } from "next/router";
import useAuth from "../../hooks/useAuth";
import dynamic from "next/dynamic";
const Lottie = dynamic(() => import("lottie-react-web"), { ssr: false });
import lockAnimation from "../../public/assets/lottie/lock-animation.json";
import { UserContext } from "../../context";

interface BlazeSessionsLoginCardProps {
  message: {
    text: string;
    metadata?: {
      english_answer?: string;
      hinglish_answer?: string;
      detected_language?: string;
      suggested_follow_ups?: string[];
    };
  };
  onLoginSuccess?: () => void;
}

const BlazeSessionsLoginCard: React.FC<BlazeSessionsLoginCardProps> = ({
  message,
  onLoginSuccess,
}) => {
  const router = useRouter();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [showLoadingState, setShowLoadingState] = useState(false);
  const { user, checkedLoggedInStatus } = useAuth();
  const [, setIsSliderOpen] = useContext(UserContext).isSliderOpen;

  // Check authentication status whenever it changes
  useEffect(() => {
    if (checkedLoggedInStatus) {
      setIsLoggedIn(!!user && (user as any).uid !== "guest-user");
    }
  }, [user, checkedLoggedInStatus]);

  // Show success animation when user logs in, followed by loading state
  useEffect(() => {
    if (isLoggedIn && onLoginSuccess) {
      // First show success animation
      setShowSuccessAnimation(true);

      // After animation completes, switch to loading state
      const successTimer = setTimeout(() => {
        setShowSuccessAnimation(false);
        setShowLoadingState(true);

        // Then call the success callback after a short delay
        const loadingTimer = setTimeout(() => {
          onLoginSuccess();
        }, 1500);

        return () => clearTimeout(loadingTimer);
      }, 2000);

      return () => clearTimeout(successTimer);
    }
  }, [isLoggedIn, onLoginSuccess]);

  // Handle login button click
  const handleLoginClick = () => {
    // Save current path to redirect back after login
    // localStorage.setItem("loginRedirectPath", router.asPath);
    setIsSliderOpen("sign-in");
  };

  // Determine which message to show based on language
  const displayText =
    message.metadata?.detected_language === "hinglish" ||
    message.metadata?.detected_language === "hindi"
      ? message.metadata?.hinglish_answer || message.text
      : message.metadata?.english_answer || message.text;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="blaze-login-card"
      style={{
        borderRadius: "10px",
        overflow: "hidden",
        position: "relative",
        backgroundColor: "rgba(15, 23, 42, 0.7)",
        backdropFilter: "blur(10px)",
        border: "1px solid rgba(180, 30, 30, 0.3)",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
        padding: "14px",
        maxWidth: "500px",
        margin: "6px 0",
      }}
    >
      {/* If showing success animation */}
      {showSuccessAnimation ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="success-animation"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "10px",
          }}
        >
          <div className="checkmark-circle" style={{ marginBottom: "12px" }}>
            <motion.div
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              style={{
                width: "50px",
                height: "50px",
                borderRadius: "50%",
                background: "linear-gradient(135deg, #c71c1c, #8c0000)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg
                width="25"
                height="25"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </motion.div>
          </div>
          <p
            style={{
              color: "white",
              fontSize: "0.9rem",
              fontWeight: 500,
              textAlign: "center",
            }}
          >
            Login successful!
          </p>
        </motion.div>
      ) : showLoadingState ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "10px",
          }}
        >
          <div className="checkmark-circle" style={{ marginBottom: "12px" }}>
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.3 }}
              style={{
                width: "50px",
                height: "50px",
                borderRadius: "50%",
                background: "linear-gradient(135deg, #c71c1c, #8c0000)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg
                width="25"
                height="25"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </motion.div>
          </div>

          <p
            style={{
              color: "white",
              fontSize: "0.95rem",
              fontWeight: 600,
              marginBottom: "8px",
              textAlign: "center",
            }}
          >
            Login Successful!
          </p>

          <p
            style={{
              color: "rgba(255, 255, 255, 0.8)",
              fontSize: "0.85rem",
              textAlign: "center",
              lineHeight: 1.5,
            }}
          >
            You can now ask about your Blaze sessions, schedule a new tutoring
            session, or get help with your studies.
          </p>
        </motion.div>
      ) : (
        <div style={{ display: "flex", alignItems: "center" }}>
          <div
            style={{
              width: "45px",
              height: "45px",
              marginRight: "12px",
              flexShrink: 0,
            }}
            className="lock-animation"
          >
            <Lottie
              options={{
                animationData: lockAnimation,
                loop: true,
              }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <h3
              style={{
                color: "white",
                fontSize: "1rem",
                fontWeight: 600,
                margin: 0,
                marginBottom: "3px",
              }}
            >
              Sign in Required
            </h3>
            <p
              style={{
                color: "rgba(255, 255, 255, 0.8)",
                fontSize: "0.85rem",
                margin: 0,
                lineHeight: 1.4,
              }}
            >
              {displayText}
            </p>

            <button
              onClick={handleLoginClick}
              style={{
                background: "linear-gradient(135deg, #c71c1c, #8c0000)",
                color: "white",
                border: "none",
                padding: "8px 16px",
                borderRadius: "6px",
                fontWeight: 500,
                fontSize: "0.85rem",
                cursor: "pointer",
                transition: "all 0.2s ease",
                marginTop: "10px",
                display: "flex",
                alignItems: "center",
              }}
              className="login-button"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ marginRight: "6px" }}
              >
                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                <polyline points="10 17 15 12 10 7" />
                <line x1="15" y1="12" x2="3" y2="12" />
              </svg>
              Sign in
            </button>
          </div>
        </div>
      )}

      <style jsx global>{`
        .login-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(140, 0, 0, 0.4);
        }

        .lock-animation {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        @keyframes success-pulse {
          0% {
            box-shadow: 0 0 0 0 rgba(140, 0, 0, 0.7);
          }
          70% {
            box-shadow: 0 0 0 10px rgba(140, 0, 0, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(140, 0, 0, 0);
          }
        }

        .success-animation .checkmark-circle {
          animation: success-pulse 2s infinite;
        }
      `}</style>
    </motion.div>
  );
};

export default BlazeSessionsLoginCard;
